import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { SITE_DATA } from '../data/Constants';
import { optimizeImage, generateSrcSet } from '../utils';
import SEO from '../components/SEO';
import Schema from '../components/Schema';

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = (error) => reject(error);
  });
};

const ContactPage = () => {
  const location = useLocation();
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    interest: '',
    budget: '',
    message: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check for "Get Quote" data passed from Product Page
  useEffect(() => {
    if (location.state && location.state.productOfInterest) {
      const { productOfInterest, sku } = location.state;
      setFormData(prev => ({
        ...prev,
        interest: `Quote: ${productOfInterest} (SKU: ${sku})`,
        message: `Hi, I am interested in getting a quote and checking availability for the ${productOfInterest}.`
      }));

      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- GOOGLE SHEETS BACKEND LOGIC ---
  const GOOGLE_SCRIPT_URL = import.meta.env.VITE_GOOGLE_SCRIPT_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let base64File = "";
      let fileName = "";
      let mimeType = "";

      if (selectedFile) {
        if (selectedFile.size > 5 * 1024 * 1024) {
          alert("File size exceeds 5MB limit. Please choose a smaller image.");
          setIsLoading(false);
          return;
        }
        base64File = await fileToBase64(selectedFile);
        fileName = selectedFile.name;
        mimeType = selectedFile.type;
      }

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify({
          ...formData,
          date: new Date().toLocaleString(),
          fileData: base64File,
          fileName: fileName,
          mimeType: mimeType
        }),
      });

      const result = await response.json();

      if (result.result === "success") {
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }

        // Send event to Google Tag Manager (GTM)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'form_submission',
          formName: 'contact_form'
        });

        setIsSubmitted(true);
      } else {
        throw new Error(result.error || "Failed to save to Google Sheets");
      }
    } catch (error) {
      console.error("Error submitting form", error);
      alert("There was an error sending your message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='min-h-screen pt-[72px] bg-white animate-fade-in'>
      <SEO
        title="Contact Us | Langley Showroom"
        description="Get in touch with our heating experts for consultations, quotes, and support. Visit our extensive fireplace and grill showroom located in Langley, BC."
        keywords="contact mainland fireplaces, fireplace showroom langley, fireplace service langley, fireplace quote"
      />
      <Schema type="LocalBusiness" />
      {/* HEADER SECTION */}
      <section className='relative w-full h-[40vh] min-h-[300px] flex items-center justify-center bg-secondary overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src={optimizeImage(`${import.meta.env.BASE_URL}images/headers/contact.avif`, 1200)}
            srcSet={generateSrcSet(`${import.meta.env.BASE_URL}images/headers/contact.avif`)}
            sizes="100vw"
            alt="Mainland Fireplaces Langley showroom — contact us for fireplace and grill consultations"
            width={1200}
            height={800}
            fetchPriority="high"
            className='w-full h-full object-cover opacity-30'
          />
          <div className='absolute inset-0 bg-linear-to-t from-secondary via-secondary/70 to-transparent'></div>
        </div>
        <div className='relative z-10 text-center px-6'>
          <div className='flex items-center justify-center gap-4 mb-4'>
            <span className='h-px w-12 bg-primary'></span>
            <span className='text-primary font-bold tracking-[0.25em] uppercase text-xs animate-in slide-in-from-bottom duration-700'>
              Get In Touch
            </span>
            <span className='h-px w-12 bg-primary'></span>
          </div>
          <h1 className='font-heading text-5xl md:text-7xl text-white uppercase leading-none drop-shadow-lg animate-in fade-in zoom-in duration-1000'>
            Contact <span className='text-primary'>Us</span>
          </h1>
        </div>
      </section>

      {/* CONTACT US SECTION */}
      <div className='max-w-7xl mx-auto px-6 md:px-12 py-24'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-16'>
          <div className='space-y-12'>
            <div>
              <h2 className='font-heading text-3xl md:text-4xl text-secondary uppercase mb-6'>
                We're Here <br /><span className='text-primary'>To Help</span>
              </h2>
              <p className='font-sans text-gray-700 text-lg leading-relaxed'>
                Have questions or ready to start your project? Whether you need expert advice on a new fireplace or service for an existing one, our team is ready.
              </p>
            </div>

            <div className='grid gap-6'>
              <div className='flex items-start gap-6 p-3 md:p-6 bg-gray-50 border border-gray-300 rounded-sm hover:border-primary transition-colors group'>
                <div className='w-12 h-12 bg-white flex items-center justify-center text-primary shadow-sm rounded-full shrink-0 group-hover:scale-110 transition-transform'>
                  <MapPin className='w-6 h-6' />
                </div>
                <div>
                  <h3 className='font-heading text-lg text-secondary uppercase mb-2'>Showroom</h3>
                  <p className='text-gray-700 text-sm leading-relaxed hover:text-primary'>
                    <a href={SITE_DATA.mapLink} target="_blank" rel="noopener noreferrer">
                      20771 Langley Bypass #201,<br /> Langley, BC V3A 5E8
                    </a>
                  </p>
                </div>
              </div>

              <div className='flex items-start gap-6 p-3 md:p-6 bg-gray-50 border border-gray-300 rounded-sm hover:border-primary transition-colors group'>
                <div className='w-12 h-12 bg-white flex items-center justify-center text-primary shadow-sm rounded-full shrink-0 group-hover:scale-110 transition-transform'>
                  <Phone className='w-6 h-6' />
                </div>
                <div>
                  <h3 className='font-heading text-lg text-secondary uppercase mb-2'>Contact</h3>
                  <p className='text-gray-700 text-sm mb-1'>Phone: <a href={`tel:${SITE_DATA.phone}`} className='text-secondary font-bold hover:text-primary transition-colors'>{SITE_DATA.phone}</a></p>
                  <p className='text-gray-700 text-sm'>Email: <a href={`mailto:${SITE_DATA.email}`} className='text-secondary font-bold hover:text-primary transition-colors'>{SITE_DATA.email}</a></p>
                </div>
              </div>

              <div className='flex items-start gap-6 p-3 md:p-6 bg-gray-50 border border-gray-300 rounded-sm hover:border-primary transition-colors group'>
                <div className='w-12 h-12 bg-white flex items-center justify-center text-primary shadow-sm rounded-full shrink-0 group-hover:scale-110 transition-transform'>
                  <Clock className='w-6 h-6' />
                </div>
                <div>
                  <h3 className='font-heading text-lg text-secondary uppercase mb-2'>Opening Hours</h3>
                  <p className='text-gray-700 text-sm flex justify-between w-full max-w-[250px] mb-1'>
                    <span className='mr-1'>Mon - Fri:</span> <span className='font-bold text-secondary'>09:00AM - 04:30PM</span>
                  </p>
                  <p className='text-gray-700 text-sm flex justify-between w-full max-w-[250px]'>
                    <span className='mr-1'>Saturday:</span> <span className='font-bold text-secondary'>10:00AM - 04:00PM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CONTACT FORM */}
          <div ref={formRef} className='bg-white p-8 md:p-10 border border-gray-300 shadow-xl rounded-sm relative overflow-hidden'>
            <div className='absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -z-10'></div>
            {isSubmitted ? (
              <div className='h-full flex flex-col items-center justify-center text-center py-20 animate-fade-in'>
                <div className='w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6'>
                  <CheckCircle className='w-8 h-8' />
                </div>
                <h3 className='font-heading text-2xl text-secondary uppercase mb-2'>Message Sent!</h3>
                <p className='text-gray-500 max-w-xs mx-auto'>
                  Thank you for contacting us. One of our experts will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => {
                    // Reset the form data
                    setFormData({
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      location: '',
                      interest: '',
                      budget: '',
                      message: ''
                    });
                    setSelectedFile(null);
                    setIsSubmitted(false)
                  }}
                  className='mt-8 text-primary font-bold text-sm uppercase tracking-widest hover:underline'
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className='space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div className='space-y-2'>
                    <label htmlFor="firstName" className='text-xs font-bold uppercase tracking-widest text-secondary'>First Name *</label>
                    <input
                      id="firstName"
                      type="text" name="firstName" required
                      value={formData.firstName} onChange={handleChange}
                      className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                    />
                  </div>
                  <div className='space-y-2'>
                    <label htmlFor="lastName" className='text-xs font-bold uppercase tracking-widest text-secondary'>Last Name *</label>
                    <input
                      id="lastName"
                      type="text" name="lastName" required
                      value={formData.lastName} onChange={handleChange}
                      className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div className='space-y-2'>
                    <label htmlFor="email" className='text-xs font-bold uppercase tracking-widest text-secondary'>Email *</label>
                    <input
                      id="email"
                      type="email" name="email" required
                      value={formData.email} onChange={handleChange}
                      className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                    />
                  </div>
                  <div className='space-y-2'>
                    <label htmlFor="phone" className='text-xs font-bold uppercase tracking-widest text-secondary'>Phone *</label>
                    <input
                      id="phone"
                      type="tel" name="phone" required
                      value={formData.phone} onChange={handleChange}
                      className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <label htmlFor="location" className='text-xs font-bold uppercase tracking-widest text-secondary'>Location *</label>
                  <input
                    id="location"
                    type="text" name="location" required
                    value={formData.location} onChange={handleChange}
                    placeholder="e.g., Langley, BC V3A 5E8"
                    className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                  />
                </div>

                <div className='space-y-2'>
                  <label htmlFor="interest" className='text-xs font-bold uppercase tracking-widest text-secondary'>Interest / Subject *</label>
                  <input
                    id="interest"
                    type="text" name="interest" required
                    value={formData.interest} onChange={handleChange}
                    placeholder="e.g., Gas Fireplace Quote"
                    className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                  />
                </div>

                <div className='space-y-2'>
                  <label htmlFor="message" className='text-xs font-bold uppercase tracking-widest text-secondary'>Message *</label>
                  <textarea
                    id="message"
                    name="message" rows="4" required
                    value={formData.message} onChange={handleChange}
                    className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm'
                  ></textarea>
                </div>

                <div className='space-y-2'>
                  <label htmlFor="imageUpload" className='text-xs font-bold uppercase tracking-widest text-secondary'>Upload Image (Optional)</label>
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const file = e.target.files[0];
                        if (!file.type.startsWith('image/')) {
                          alert('Security Error: Invalid file type. Only JPG, PNG, GIF, and WEBP images are permitted.');
                          e.target.value = ''; // Reset the input
                          setSelectedFile(null);
                          return;
                        }
                        setSelectedFile(file);
                      } else {
                        setSelectedFile(null);
                      }
                    }}
                    className='w-full p-3 bg-gray-50 border border-gray-200 focus:border-primary focus:outline-hidden transition-colors rounded-sm text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-widest file:bg-gray-200 file:text-secondary hover:file:bg-gray-300 transition-colors cursor-pointer'
                  />
                  {selectedFile && <p className="text-xs text-primary mt-1">Selected: {selectedFile.name}</p>}
                </div>

                <div className='text-xs text-gray-500 pb-2 text-center'>
                  By sending a message, you agree to our <Link to="/terms-and-conditions" className="text-primary hover:underline">Terms and Conditions</Link> and <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                </div>

                <button
                  type='submit'
                  disabled={isLoading}
                  className='w-full py-4 bg-primary text-white uppercase font-bold tracking-widest rounded-sm text-xs hover:bg-secondary transition-colors shadow-lg flex items-center justify-center gap-2 group'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='w-4 h-4 animate-spin' /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* LOCATION MAP SECTION */}
      <section className='w-full h-[400px] bg-gray-200 transition-all duration-700'>
        <iframe
          title="Mainland Fireplaces Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2611.5909193059015!2d-122.64993832398424!3d49.1134114827266!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485ce288b2e1ba1%3A0xd85fe8414bfc2d81!2sMainland%20Fireplaces!5e0!3m2!1sen!2sca!4v1764825723971!5m2!1sen!2sca"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>
    </div>
  );
};

export default ContactPage;